import { DefaultRawDatum } from '@nivo/pie';
import arraySort from 'array-sort';
import {
  ContentQualityQuestionData,
  ContentQualityResponseData,
  ContentQualityResponseOptions,
  QuestionResponse,
  QuestionnaireResponse,
  SequencedQuestionResponse,
} from '@amzn/awscat-pattern-library-service-model';
import { ScoreBarometerSegmentType } from '../../model/Enum';
import { barometerSegmentColors } from './DisplayColors';

/* Type definitions and constants*/

export interface CategoryOrder {
  category: string;
  smallestSequence: number;
}

export const ACSII_VALUE_FOR_A = 97;

/* Handling maps in questionnaire form */

// static question maps
export const populateQuestionMaps = (
  questions: ContentQualityQuestionData[],
  questionIdToSequenceMap: Map<string, number>, // <-- to populate
  questionSequenceToQuestionMap: Map<number, ContentQualityQuestionData>, // <-- to populate
  questionCategoryToSequencesMap: Map<string, number[]>, // <-- to populate
) => {
  questions.forEach((question) => {
    const { questionId, questionSequence, category } = question;
    const sequenceNumber = Number(questionSequence);
    // question id to sequence - for getting question dependencies and visibility
    questionIdToSequenceMap.set(questionId, sequenceNumber);
    // question sequence to question - for display each question
    questionSequenceToQuestionMap.set(sequenceNumber, question);
    // category to sequences - for grouping questions under categories
    let sequencesForCategory: number[] = [];
    if (questionCategoryToSequencesMap.has(category)) {
      const sequences = questionCategoryToSequencesMap.get(category);
      sequencesForCategory = !!sequences ? (sequences as number[]) : [];
    }
    sequencesForCategory.push(sequenceNumber);
    questionCategoryToSequencesMap.set(category, sequencesForCategory);
  });
};

// static category
export const createSortedCategories = (
  questionCategoryToSequencesMap: Map<string, number[]>,
  questionCategories: CategoryOrder[], // <-- to populate
) => {
  // sort sequences under each category
  questionCategoryToSequencesMap.forEach((sequences, category) => {
    arraySort<number>(sequences, 'sequences');
    questionCategories.push({
      category,
      // @ts-expect-error TS2322 - strict migration
      smallestSequence: sequences[0],
    });
  });
  // display categories in order
  arraySort(questionCategories, 'smallestSequence');
};

// static dependent map
export const populateDependentMap = (
  idToSequenceMap: Map<string, number>,
  sequenceToQuestionMap: Map<number, ContentQualityQuestionData>,
  dependentMap: Map<number, number[]>, // <-- to populate
) => {
  [...sequenceToQuestionMap.entries()].forEach((entry) => {
    // for each question, find all its dependencies
    const [sequence, question] = entry;
    const { dependency } = question;
    if (dependency) {
      [...dependency].forEach((dependcyId) => {
        // for each dependency, update its dependent map entry
        const dependencySequence = idToSequenceMap.get(dependcyId) || -1;
        let dependents: number[] = [];
        if (dependentMap.has(dependencySequence)) {
          dependents = dependentMap.get(dependencySequence) || [];
        }
        dependents.push(sequence);
        dependentMap.set(dependencySequence, dependents);
      });
    }
  });
};

export const updateVisibilityMap = (
  idToSequenceMap: Map<string, number>,
  sequenceToQuestionMap: Map<number, ContentQualityQuestionData>,
  sequenceToResponseMap: Map<number, QuestionResponse>,
  visibilityMap: Map<number, boolean>, // <-- to update
) => {
  [...sequenceToQuestionMap.entries()].forEach((entry) => {
    // for each question, find all its dependencies
    const [sequence, question] = entry;
    const { dependency } = question;
    let shouldDisplay = true;
    if (dependency) {
      // check if all dependencies have responses as not N/A
      shouldDisplay = [...dependency].every((dependcyId) => {
        const dependencySequence = idToSequenceMap.get(dependcyId) || -1;
        return (
          sequenceToResponseMap?.has(dependencySequence) &&
          sequenceToResponseMap?.get(dependencySequence)?.response !==
            ContentQualityResponseOptions.NA
        );
      });
    }
    visibilityMap.set(sequence, shouldDisplay);
  });
};

export const responsesArrayToMap = (sequencedResponses: SequencedQuestionResponse[]) => {
  const sequenceToResponseMap: Map<number, QuestionResponse> = new Map<number, QuestionResponse>();
  sequencedResponses.forEach((r) => {
    const { sequence, response } = r;
    sequenceToResponseMap.set(sequence, response);
  });
  return sequenceToResponseMap;
};

export const responsesMapToArray = (
  sequenceToResponseMap: Map<number, QuestionResponse>,
): SequencedQuestionResponse[] => {
  const responsesArray: SequencedQuestionResponse[] = [];
  [...sequenceToResponseMap.entries()].forEach((entry) => {
    const [sequence, response] = entry;
    responsesArray.push({ sequence, response });
  });
  return responsesArray;
};

// return true if should update visibility map
export const updateDependentResponses = (
  sequence: number,
  response: ContentQualityResponseOptions,
  dependentMap: Map<number, number[]>,
  responseMap: Map<number, QuestionResponse>, // <-- to update
) => {
  let visibilityMapShouldUpdate = false;
  if (dependentMap?.has(sequence)) {
    visibilityMapShouldUpdate = true;
    // find all of its dependents
    const dependents = dependentMap.get(sequence) || [];
    dependents.forEach((dependent) => {
      // if response N/A then delete its dependent's response
      if (response === ContentQualityResponseOptions.NA) {
        responseMap.delete(dependent);
      }
    });
  }
  return visibilityMapShouldUpdate;
};

/* Handling maps in evaluation card */

export const populateEvaluationMaps = (
  evaluations: ContentQualityResponseData[],
  questionSequenceToQuestionMap: Map<number, ContentQualityQuestionData>, // <-- to populate
  questionCategoryToSequencesMap: Map<string, number[]>, // <-- to populate
  questionSequenceToResponseMap: Map<number, QuestionResponse>, // <-- to populate
) => {
  evaluations.forEach((evaluation) => {
    const { questionSequence, questionBody, category, hyperlinks, response, comment } = evaluation;
    const sequenceNumber = Number(questionSequence);
    // question sequence to question - for display each question
    questionSequenceToQuestionMap.set(
      sequenceNumber,
      ContentQualityQuestionData.create(
        '', // not needed in readonly evaluation card
        {
          questionSequence,
          questionBody,
          category,
          hyperlinks,
          responseOptions: new Set<ContentQualityResponseOptions>(), // not needed in readonly evaluation card
        },
      ),
    );
    // category to sequences - for grouping questions under categories
    let sequencesForCategory: number[] = [];
    if (questionCategoryToSequencesMap.has(category)) {
      const sequences = questionCategoryToSequencesMap.get(category);
      sequencesForCategory = !!sequences ? (sequences as number[]) : [];
    }
    sequencesForCategory.push(sequenceNumber);
    questionCategoryToSequencesMap.set(category, sequencesForCategory);
    // sequence to response - for display response
    questionSequenceToResponseMap.set(sequenceNumber, { response, comment });
  });
};

/* Quality score */

export const calculateQualityScore = (
  responseMap: Map<number, QuestionResponse>,
  visibilityMap: Map<number, boolean>,
) => {
  let maxScore = 0;
  let actualScore = 0;

  for (const entry of [...visibilityMap.entries()]) {
    const [sequence, required] = entry;
    const response = responseMap?.get(sequence)?.response;
    if (required && !response) {
      return -1;
    }
    if (response && response !== ContentQualityResponseOptions.NA) {
      maxScore++;
    }
    if (response === ContentQualityResponseOptions.YES) {
      actualScore++;
    }
  }

  return Math.round((actualScore / maxScore) * 100);
};

/* Barometer */

const MIN = 0;
const LOW = 40;
const HIGH = 70;
const MAX = 100;

const lowZeroData: DefaultRawDatum = {
  id: ScoreBarometerSegmentType.LowZero,
  value: LOW,
};

const medZeroData: DefaultRawDatum = {
  id: ScoreBarometerSegmentType.MedZero,
  value: HIGH - LOW,
};

const hiZeroData: DefaultRawDatum = {
  id: ScoreBarometerSegmentType.HiZero,
  value: MAX - HIGH,
};

const lowData: DefaultRawDatum = {
  id: ScoreBarometerSegmentType.Low,
  value: LOW,
};

const medData: DefaultRawDatum = {
  id: ScoreBarometerSegmentType.Med,
  value: HIGH - LOW,
};

export const getDataForBarometer = (score: number) => {
  let data: DefaultRawDatum[] = [];
  if (score < 0) {
    data = [lowZeroData, medZeroData, hiZeroData];
  } else if (score < LOW) {
    data = [
      {
        id: ScoreBarometerSegmentType.Low,
        value: score,
      },
      {
        id: ScoreBarometerSegmentType.LowZero,
        value: LOW - score,
      },
      medZeroData,
      hiZeroData,
    ];
  } else if (score < HIGH) {
    data = [
      lowData,
      {
        id: ScoreBarometerSegmentType.Med,
        value: score - LOW,
      },
      {
        id: ScoreBarometerSegmentType.MedZero,
        value: HIGH - score,
      },
      hiZeroData,
    ];
  } else {
    data = [
      lowData,
      medData,
      {
        id: ScoreBarometerSegmentType.Hi,
        value: score - HIGH,
      },
      {
        id: ScoreBarometerSegmentType.HiZero,
        value: MAX - score,
      },
    ];
  }
  return data;
};

export const getColorsForBarometer = (data: DefaultRawDatum[]) => {
  return data?.map((d) => barometerSegmentColors.get(d.id as ScoreBarometerSegmentType));
};

/* Questionnaire Controls */

// construct responses
export const createQuestionnaireResponses = (
  idToSequenceMap: Map<string, number>,
  sequenceToResponseMap: Map<number, QuestionResponse>,
) => {
  const responses: QuestionnaireResponse[] = [];
  [...idToSequenceMap.entries()].forEach((entry) => {
    const [id, sequence] = entry;
    const questionResponse = sequenceToResponseMap.get(sequence);
    if (!!questionResponse) {
      const { response, comment } = questionResponse;
      responses.push(
        Object.assign(new QuestionnaireResponse(), {
          questionId: id,
          questionSequence: sequence + '',
          response,
          comment,
        }),
      );
    }
  });
  return responses;
};

// validate comment
export const hasEmptyComment = (sequenceToErrorMap: Map<number, string>) => {
  for (const entry of sequenceToErrorMap.entries()) {
    const [sequence, error] = entry;
    if (!!error) {
      return true;
    }
  }
  return false;
};

// validate score
export const isScoreValid = (score: number) => {
  return score >= 0 && score <= MAX;
};

export const REVIEW_COMMENT_MAX = 400;
export const COMMENT_MAX = 300;
export const BR_REVIEW_GUIDELINES_PATTERN_LINK =
  'https://w.amazon.com/bin/view/AWS/Teams/GlobalServices/S%26O/APGBarRaiser/BRReviewGuide/';
export const BR_REVIEW_GUIDELINES_GUIDE_LINK =
  'https://w.amazon.com/bin/view/AWS/Teams/GlobalServices/S&O/APGBarRaiser/BRReviewGuide/Guides/';
export const BR_REVIEW_GUIDELINES_STRATEGY_LINK =
  'https://w.amazon.com/bin/view/AWS/Teams/GlobalServices/S%26O/APGBarRaiser/BRReviewGuide/Strategy/';
